import {gsap} from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";

class AnimatedFeature {
    constructor(el) {
        this.section = el;
        this.images         = this.section.querySelectorAll('.animated-feature__img');
        this.heading        = this.section.querySelector('.animated-feature__heading');
        this.text           = this.section.querySelector('.animated-feature__subheading');
        this.content        = this.section.querySelector('.animated-feature__content');

        this.timeline = null;
        this.introTimeline = null;
        this.isAnimated = false;

        this.desktopContext = null;
        this.mobileContext = null;
        this.currIsMobile = window.innerWidth < 768;

        this.init();
    }

    init() {
        // Set up ScrollTrigger
        this.setupScrollTrigger();

        // Make section fixed content visible again after animations have been set up.
        // This prevents "flash of unstyled content" (FOUC) of these elements on page load.
        gsap.set(this.section, { visibility: 'visible' });
    }

    setupScrollTrigger() {

        // Animation that runs before the main timeline gets pinned 
        this.introTimeline = gsap.timeline({
            scrollTrigger: {
                trigger: this.heading,
                start: "top 80%",
                endTrigger: this.section,
                scrub: true,
                markers: false,
                pinSpacing: false
            }
        });

        // Create main timeline
        this.timeline = gsap.timeline({
            onComplete: () => {
                this.isAnimated = true;
            }
        });


         // Build animation sequence
        if (this.currIsMobile) {
            this.buildMobileAnimation();
        } else {
            this.buildAnimation();
        }

        //Pinning action
        ScrollTrigger.create({
            trigger: this.section,
            start: "top top",
            end: "bottom top",
            pin: true,
            anticipatePin: 1,
            animation: this.timeline,
            scrub: true,
            markers: false,
            pinSpacing: true
        });
    }

    buildAnimation() {
        this.desktopContext = gsap.context(() => {
            gsap.set(this.heading, { opacity: 1 });
            gsap.set(this.images, { xPercent: -50, top: '20%', left:'50%', y: '100vh', scale: 0.8 });

            this.introTimeline.fromTo(this.heading, { y: '0', yPercent: 0 }, { duration: 2, y: '50vh', yPercent: -50, ease: 'power2.out' });

            // Step 1: Images pop up one by one to center (longer duration)
            //this.timeline.to(this.heading, { duration: 3.2, y:'50vh', ease: 'power1.out' }, 0);
            this.images.forEach((img, index) => {
                this.timeline.to(img, {
                    duration: 2.5, // Longer duration
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    ease: "back.inOut",
                    delay: index * 0.5 // More delay between images
                }, 1.5);
            });

            // Step 2: Longer pause at center
            //this.timeline.to({}, { duration: 1.5 }, "+=0.5");

            // Step 3: Fan out to pentagon positions (slower)
            this.timeline.to(this.images[1], { // bottom-left
                    duration: 3,
                    x: "-25vw",
                    y: "40vh",
                    ease: "power1.inOut"
                })
                .to(this.images[2], { // bottom-right
                    duration: 3,
                    x: "15vw",
                    y: "60vh",
                    ease: "power1.inOut"
                })
                .to(this.images[3], { // centre-right
                    duration: 3,
                    x: "32vw",
                    y: "30vh",
                    ease: "power1.inOut"
                })
                .to(this.images[4], { // top-right
                    duration: 3,
                    x: "30vw",
                    y: "-25vh",
                    ease: "power1.inOut"
                })
                .to(this.images[5], { // top-left
                    duration: 3,
                    x: "-35vw",
                    y: "-30vh",
                    ease: "power1.inOut"
                });

            // Step 4: Move heading up slightly (with more time)
            /* this.timeline.to(this.heading, {
                duration: 1.5,
                y: "25vh",
                ease: "power2.out"
            }); */

            // Step 5: Show text content (slower fade)
            this.timeline.to(this.text, {
                duration: 1.5,
                opacity: 1,
                ease: "power2.out"
            });

            // Step 6: Final pause to admire the layout
            //this.timeline.to({}, { duration: 1 }, "+=0.5");
        });
    }

    // Method to handle responsive adjustments
    handleResize() {
        ScrollTrigger.refresh();
        let newIsMobile = window.innerWidth < 768;
        if (this.currIsMobile !== newIsMobile) {
            // Animation build changes
            this.timeline.clear();
            this.introTimeline.clear();
            this.currIsMobile = newIsMobile;
            if (this.currIsMobile) {
                this.desktopContext?.revert();
                this.buildMobileAnimation();
            } else {
                this.mobileContext?.revert();
                this.buildAnimation();
            }
        }
    }

    buildMobileAnimation() {
        this.mobileContext = gsap.context(() => {
            gsap.set(this.heading, { opacity: 1 });
            gsap.set(this.images, { xPercent: -50, top: '20%', left:'50%', y: '100vh', scale: 0.8 });
            
            this.introTimeline.fromTo(this.heading, { y: '0', yPercent: 0 }, { duration: 2, y: '50vh', yPercent: -50, ease: 'power2.out' });
            
            // Mobile animation with adjusted timing
            this.images.forEach((img, index) => {
                this.timeline.to(img, {
                    duration: 2.5,
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    ease: "back.inOut",
                    delay: index * 0.25
                }, 0);
            });

            //this.timeline.to({}, { duration: 1 }, "+=0.5");

            // Mobile fan-out with smaller distances
            this.timeline.to(this.images[1], { // bottom-left
                    duration: 3,
                    x: "-30vw",
                    y: "35vh",
                    ease: "power2.out"
                })
                .to(this.images[2], { // bottom-right
                    duration: 3,
                    x: "20vw",
                    y: "55vh",
                    ease: "power2.out"
                })
                .to(this.images[3], { // centre-right
                    duration: 3,
                    x: "35vw",
                    y: "30vh",
                    ease: "power2.out"
                })
                .to(this.images[4], { // top-right
                    duration: 3,
                    x: "30vw",
                    y: "-25vh",
                    ease: "power2.out"
                })
                .to(this.images[5], { // top-left
                    duration: 3,
                    x: "-30vw",
                    y: "-30vh",
                    ease: "power2.out"
                });

            this.timeline.to(this.text, {
                duration: 1.5,
                opacity: 1,
                ease: "power2.out"
            });

            //this.timeline.to({}, { duration: 1 }, "+=0.5");
        });
    }

    // Destroy method for cleanup
    destroy() {
        if (this.timeline) {
            this.timeline.kill();
        }
        if (this.introTimeline) {
            this.introTimeline.kill();
        }
        //ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    let elements = document.querySelectorAll(".js-animated-feature");

    if (!elements || elements.length === 0) {
        return; // Stop execution on pages without the module.
    }

    elements.forEach((el) => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);

            // Initialize the animated feature
            const animatedFeature = new AnimatedFeature(el);

            // Handle window resize
            let resizeTimer;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(() => {
                animatedFeature.handleResize();
                }, 250);
            });
            /* window.addEventListener('resize', () => {
                animatedFeature.handleResize();
            }); */
        } else {
            console.warn('GSAP or ScrollTrigger not loaded. Animation will not work.');
        }
    });
});
